import { LuLoaderCircle, LuSearch, LuPlus } from "react-icons/lu";
import { useState, useEffect, useMemo, useRef, memo } from "react";
import { createPortal } from "react-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BrandAd, DefaultPrompt, DefaultPromptsResponse } from "@/screens/analysis/types";
import { BaseActionProps } from "@/components/common/adCard/types";
import { useGlobalStore } from "@/GlobalStore";
import { cn } from "@/lib/utils";
import {
  RiAspectRatioLine,
  RiLayoutColumnLine,
  RiLayoutGridLine,
  RiSquareLine,
  RiArrowGoBackLine,
  RiCheckLine,
  RiArrowGoBackFill,
  RiPencilLine,
  RiBrushAiLine,
  RiApps2AiLine,
  RiQuillPenAiLine,
  RemixiconComponentType,
  RiGeminiLine,
  RiOpenaiLine,
  RiSettingsLine,
} from "@remixicon/react";
import { LuChevronDown } from "react-icons/lu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  createDefaultPrompt,
  updateDefaultPrompt,
  deleteDefaultPrompt,
  fetchDefaultPrompts,
} from "@/screens/analysis/apiService";
import { RiEditLine, RiDeleteBinLine } from "@remixicon/react";
import { SqButton } from "@/components/squircle-ui/button";
import { Portal } from "@radix-ui/react-tooltip";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
  CREATIVE_LIBRARY_STORAGE_KEYS,
} from "@/utils/localStorage";
import type { Moodboard } from "@/screens/creative-library/types";

// Types for settings
type Model = "gpt-image-1" | "gpt-image-1.5" | "nano-banana-pro";
type AspectRatio = "Auto" | "9:16" | "2:3" | "4:5" | "1:1" | "3:2" | "16:9";
type Variations = 1 | 2 | 4;

interface CreateAdProps extends BaseActionProps {
  ad: BrandAd;
  onCreateAd?: (settings: CreateAdSettings) => Promise<void>;
  onSelectAd?: () => void;
  // Current settings from CreativeInputSection for sync
  currentModel?: Model;
  currentAspectRatio?: AspectRatio;
  currentVariations?: Variations;
  currentDefaultPromptId?: string | null;
  currentMoodboardId?: string | null;
  moodboards?: Moodboard[];
  moodboardsLoading?: boolean;
  onCreateMoodboard?: () => void;
  onEditMoodboard?: (moodboardId: string) => void;
  onSettingsChange?: (settings: {
    model: Model;
    aspectRatio: AspectRatio;
    variations: Variations;
    defaultPromptId: string | null;
    moodboardId: string | null;
  }) => void;
}

export interface CreateAdSettings {
  defaultPromptId: string | null;
  model: Model;
  aspectRatio: AspectRatio;
  variations: Variations;
  moodboardId: string | null;
}

// Model options
const MODEL_OPTIONS: { value: Model; label: string; icon: RemixiconComponentType }[] = [
  {
    value: "gpt-image-1",
    label: "GPT Image 1",
    icon: RiOpenaiLine,
  },
  {
    value: "gpt-image-1.5",
    label: "GPT Image 1.5",
    icon: RiOpenaiLine,
  },
  {
    value: "nano-banana-pro",
    label: "Nano Banana Pro",
    icon: RiGeminiLine,
  },
];

// Aspect ratio options
const ASPECT_RATIO_OPTIONS: AspectRatio[] = [
  "Auto",
  "9:16",
  "2:3",
  "4:5",
  "1:1",
  "3:2",
  "16:9",
];

// Variations options
const VARIATIONS_OPTIONS: {
  label: string;
  value: Variations;
  icon: RemixiconComponentType;
}[] = [
  {
    label: "4 Variations",
    value: 4,
    icon: RiLayoutGridLine,
  },
  {
    label: "2 Variations",
    value: 2,
    icon: RiLayoutColumnLine,
  },
  {
    label: "1 Variation",
    value: 1,
    icon: RiSquareLine,
  },
];

const CreateAd = ({
  onCreateAd,
  forcedVisibility,
  onSelectAd,
  currentModel,
  currentAspectRatio,
  currentVariations,
  currentDefaultPromptId,
  currentMoodboardId,
  moodboards = [],
  moodboardsLoading = false,
  onCreateMoodboard,
  onEditMoodboard,
  onSettingsChange,
}: CreateAdProps) => {
  const [isRecreating, setIsRecreating] = useState(false);
  const { selectedAccount } = useGlobalStore();

  // Ref for finding the Media container ancestor (for glow portal)
  const createAdRef = useRef<HTMLDivElement>(null);
  const [mediaContainer, setMediaContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (createAdRef.current) {
      const container = createAdRef.current.closest<HTMLElement>(".overflow-hidden");
      if (container) setMediaContainer(container);
    }
  }, []);

  // Default prompts state - fetched internally
  const [recommendedPrompts, setRecommendedPrompts] = useState<DefaultPrompt[]>([]);
  const [createdPrompts, setCreatedPrompts] = useState<DefaultPrompt[]>([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const hasFetchedPromptsRef = useRef(false);

  // Settings state - initialize from props (if provided) or localStorage (same defaults as CreateDashboard)
  const [model, setModel] = useState<Model>(() => {
    if (currentModel !== undefined) return currentModel;
    const stored = loadFromLocalStorage<string>(
      CREATIVE_LIBRARY_STORAGE_KEYS.MODEL,
      "gpt-image-1",
    );
    if (stored === "gpt") return "gpt-image-1";
    if (stored === "gpt-image-1" || stored === "gpt-image-1.5" || stored === "nano-banana-pro")
      return stored;
    return "gpt-image-1";
  });
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(() => {
    if (currentAspectRatio !== undefined) return currentAspectRatio;
    return (
      loadFromLocalStorage<AspectRatio>(
        CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO,
        "Auto",
      ) || "Auto"
    );
  });
  const [variations, setVariations] = useState<Variations>(() => {
    if (currentVariations !== undefined) return currentVariations;
    return (
      loadFromLocalStorage<Variations>(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, 4) || 4
    );
  });

  // Sync with props when they change (only if value actually changed to prevent loops)
  useEffect(() => {
    if (currentModel !== undefined && currentModel !== model) {
      setModel(currentModel);
      saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, currentModel);
    }
  }, [currentModel, model]);

  useEffect(() => {
    if (currentAspectRatio !== undefined && currentAspectRatio !== aspectRatio) {
      setAspectRatio(currentAspectRatio);
      saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO, currentAspectRatio);
    }
  }, [currentAspectRatio, aspectRatio]);

  useEffect(() => {
    if (currentVariations !== undefined && currentVariations !== variations) {
      setVariations(currentVariations);
      saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, currentVariations);
    }
  }, [currentVariations, variations]);

  // Fetch default prompts
  const loadPrompts = async (): Promise<DefaultPromptsResponse | null> => {
    if (!selectedAccount?.id || isLoadingPrompts) return null;
    setIsLoadingPrompts(true);
    try {
      const response: DefaultPromptsResponse = await fetchDefaultPrompts({
        accountId: selectedAccount.id,
      });
      setRecommendedPrompts(response.recommended);
      setCreatedPrompts(response.created);
      hasFetchedPromptsRef.current = true;
      return response;
    } catch (error) {
      console.error("Failed to fetch default prompts:", error);
      return null;
    } finally {
      setIsLoadingPrompts(false);
    }
  };

  // Reset fetch flag when account changes
  useEffect(() => {
    hasFetchedPromptsRef.current = false;
    // Clear prompts when account changes
    setRecommendedPrompts([]);
    setCreatedPrompts([]);
  }, [selectedAccount?.id]);

  // Default prompts state (declared early for use in useEffect)
  const [selectedPromptId, setSelectedPromptId] = useState<string | null>(() => {
    if (currentDefaultPromptId !== undefined) return currentDefaultPromptId;
    return (
      loadFromLocalStorage<string | null>(
        CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID,
        null,
      ) || null
    );
  });

  useEffect(() => {
    if (
      currentDefaultPromptId !== undefined &&
      currentDefaultPromptId !== selectedPromptId
    ) {
      setSelectedPromptId(currentDefaultPromptId);
      saveToLocalStorage(
        CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID,
        currentDefaultPromptId,
      );
    }
  }, [currentDefaultPromptId, selectedPromptId]);

  // Popover states
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [aspectRatioOpen, setAspectRatioOpen] = useState(false);
  const [variationsOpen, setVariationsOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [styleSearchQuery, setStyleSearchQuery] = useState("");
  const [defaultPromptOpen, setDefaultPromptOpen] = useState(false);

  // Fetch prompts when default prompt menu opens
  useEffect(() => {
    if (defaultPromptOpen && selectedAccount?.id && !hasFetchedPromptsRef.current) {
      void loadPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPromptOpen, selectedAccount?.id]);

  const [promptSearchQuery, setPromptSearchQuery] = useState("");
  const [recommendedOpen, setRecommendedOpen] = useState(true);
  const [createdOpen, setCreatedOpen] = useState(true);
  const [isAddingNewPrompt, setIsAddingNewPrompt] = useState(false);
  const [newPromptText, setNewPromptText] = useState("");
  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const deleteTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const promptsContainerRef = useRef<HTMLDivElement>(null);
  const newPromptInputRef = useRef<HTMLInputElement>(null);
  const editPromptInputRef = useRef<HTMLInputElement>(null);
  const hasCheckedPromptValidityRef = useRef(false);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use internal prompts
  const allPrompts = useMemo(() => {
    return [...recommendedPrompts, ...createdPrompts];
  }, [recommendedPrompts, createdPrompts]);

  // Reset check flag when account changes
  useEffect(() => {
    hasCheckedPromptValidityRef.current = false;
  }, [selectedAccount?.id]);

  // Focus and select input when editing state changes
  useEffect(() => {
    if (editingPromptId && editPromptInputRef.current) {
      // Use a small timeout to ensure the input is rendered
      const timer = setTimeout(() => {
        if (editPromptInputRef.current) {
          editPromptInputRef.current.focus();
          editPromptInputRef.current.select();
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [editingPromptId]);

  // Check if localStorage prompt exists in fetched list, if not set to first item
  // Only check once when prompts are first loaded
  useEffect(() => {
    if (allPrompts.length > 0 && !hasCheckedPromptValidityRef.current) {
      hasCheckedPromptValidityRef.current = true;

      if (selectedPromptId) {
        const promptExists = allPrompts.some((p) => p.id === selectedPromptId);
        if (!promptExists) {
          // Set to first item from recommended, or first from created if no recommended
          const firstPrompt =
            recommendedPrompts.length > 0
              ? recommendedPrompts[0]
              : createdPrompts.length > 0
                ? createdPrompts[0]
                : null;
          if (firstPrompt) {
            handleDefaultPromptChange(firstPrompt.id);
          }
        }
      } else {
        // If no prompt selected, set to first item
        const firstPrompt =
          recommendedPrompts.length > 0
            ? recommendedPrompts[0]
            : createdPrompts.length > 0
              ? createdPrompts[0]
              : null;
        if (firstPrompt) {
          handleDefaultPromptChange(firstPrompt.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPrompts.length, recommendedPrompts, createdPrompts, selectedAccount?.id]);

  // Filter prompts based on search
  const filteredRecommendedPrompts = useMemo((): DefaultPrompt[] => {
    if (!promptSearchQuery.trim()) return recommendedPrompts;
    return recommendedPrompts.filter((p: DefaultPrompt) =>
      p.prompt.toLowerCase().includes(promptSearchQuery.toLowerCase()),
    );
  }, [recommendedPrompts, promptSearchQuery]);

  const filteredCreatedPrompts = useMemo((): DefaultPrompt[] => {
    if (!promptSearchQuery.trim()) return createdPrompts;
    return createdPrompts.filter((p: DefaultPrompt) =>
      p.prompt.toLowerCase().includes(promptSearchQuery.toLowerCase()),
    );
  }, [createdPrompts, promptSearchQuery]);

  // Wrapper functions to save to localStorage and sync with parent
  const handleModelChange = (newModel: Model) => {
    setModel(newModel);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.MODEL, newModel);
    onSettingsChange?.({
      model: newModel,
      aspectRatio,
      variations,
      defaultPromptId: selectedPromptId,
      moodboardId: currentMoodboardId ?? null,
    });
  };

  const handleAspectRatioChange = (newAspectRatio: AspectRatio) => {
    setAspectRatio(newAspectRatio);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.ASPECT_RATIO, newAspectRatio);
    onSettingsChange?.({
      model,
      aspectRatio: newAspectRatio,
      variations,
      defaultPromptId: selectedPromptId,
      moodboardId: currentMoodboardId ?? null,
    });
  };

  const handleVariationsChange = (newVariations: Variations) => {
    setVariations(newVariations);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.VARIATIONS, newVariations);
    onSettingsChange?.({
      model,
      aspectRatio,
      variations: newVariations,
      defaultPromptId: selectedPromptId,
      moodboardId: currentMoodboardId ?? null,
    });
  };

  const handleDefaultPromptChange = (promptId: string | null) => {
    setSelectedPromptId(promptId);
    saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID, promptId);
    onSettingsChange?.({
      model,
      aspectRatio,
      variations,
      defaultPromptId: promptId,
      moodboardId: currentMoodboardId ?? null,
    });
  };

  const handleMoodboardChange = (moodboardId: string | null) => {
    onSettingsChange?.({
      model,
      aspectRatio,
      variations,
      defaultPromptId: selectedPromptId,
      moodboardId,
    });
    setStyleSearchQuery("");
    setStyleOpen(false);
  };

  const selectedStyleLabel = useMemo(() => {
    if (!currentMoodboardId) return "None";
    return moodboards.find((m) => m.id === currentMoodboardId)?.name || "None";
  }, [currentMoodboardId, moodboards]);

  const filteredMoodboards = useMemo(() => {
    const q = styleSearchQuery.trim().toLowerCase();
    if (!q) return moodboards;
    return moodboards.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [moodboards, styleSearchQuery]);

  const shouldShowStyleSetting =
    moodboardsLoading || moodboards.length > 0 || currentMoodboardId !== undefined;

  const handleCustomPromptClick = () => {
    // Select the ad and close popovers to show CreativeInputSection
    if (onSelectAd) {
      onSelectAd();
      handleSettingsOpenChange(false);
    }
  };

  const handleSettingsOpenChange = (isOpen: boolean) => {
    setSettingsOpen(isOpen);
    forcedVisibility?.(isOpen);
    // Close sub-popovers when main settings closes
    if (!isOpen) {
      setModelOpen(false);
      setAspectRatioOpen(false);
      setVariationsOpen(false);
      setStyleOpen(false);
      setDefaultPromptOpen(false);
      setStyleSearchQuery("");
      // Cancel any ongoing edits when settings popover closes
      if (editingPromptId) {
        handleCancelEdit();
      }
      if (isAddingNewPrompt) {
        handleCancelNewPrompt();
      }
    }
  };

  const handleDefaultPromptOpenChange = (isOpen: boolean) => {
    setDefaultPromptOpen(isOpen);
    if (!isOpen) {
      setPromptSearchQuery("");
      // Cancel any ongoing edits when default prompt popover closes
      if (editingPromptId) {
        handleCancelEdit();
      }
      if (isAddingNewPrompt) {
        handleCancelNewPrompt();
      }
    }
  };

  // Helper to close all other popovers
  const closeOtherPopovers = (
    except: "model" | "aspectRatio" | "variations" | "style" | "defaultPrompt",
  ) => {
    if (except !== "model") setModelOpen(false);
    if (except !== "aspectRatio") setAspectRatioOpen(false);
    if (except !== "variations") setVariationsOpen(false);
    if (except !== "style") setStyleOpen(false);
    if (except !== "defaultPrompt") setDefaultPromptOpen(false);
  };

  // Simple hover handlers - open on hover, close others
  const handleModelHoverEnter = () => {
    closeOtherPopovers("model");
    setModelOpen(true);
  };

  const handleAspectRatioHoverEnter = () => {
    closeOtherPopovers("aspectRatio");
    setAspectRatioOpen(true);
  };

  const handleVariationsHoverEnter = () => {
    closeOtherPopovers("variations");
    setVariationsOpen(true);
  };

  const handleStyleHoverEnter = () => {
    closeOtherPopovers("style");
    setStyleOpen(true);
  };

  const handleDefaultPromptHoverEnter = () => {
    closeOtherPopovers("defaultPrompt");
    setDefaultPromptOpen(true);
  };

  const handleSelectPrompt = (promptItem: DefaultPrompt) => {
    handleDefaultPromptChange(promptItem.id);
  };

  const handleNewPrompt = () => {
    setIsAddingNewPrompt(true);
    setNewPromptText("");
    // Focus the input after it's rendered
    setTimeout(() => {
      newPromptInputRef.current?.focus();
    }, 0);
  };

  const handleCreatePrompt = async () => {
    if (!newPromptText.trim() || !selectedAccount?.id || isCreatingPrompt) return;

    setIsCreatingPrompt(true);
    try {
      const response = await createDefaultPrompt({
        accountId: selectedAccount.id,
        prompt: newPromptText.trim(),
      });
      // Add the new prompt to the created prompts list
      setCreatedPrompts((prev) => [...prev, response.data]);
      // Don't set the newly created prompt as default
      setNewPromptText("");
      setIsAddingNewPrompt(false);
      // Auto-expand created section if collapsed
      setCreatedOpen(true);
    } catch (error) {
      console.error("Failed to create prompt:", error);
    } finally {
      setIsCreatingPrompt(false);
    }
  };

  const handleEditPrompt = (promptItem: DefaultPrompt) => {
    // Clear any pending blur timeout from previous edit
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditingPromptId(promptItem.id);
  };

  const handleUpdatePrompt = async () => {
    // Read value from input ref instead of state
    const promptValue = editPromptInputRef.current?.value.trim() || "";

    if (!promptValue || !selectedAccount?.id || !editingPromptId || isUpdatingPrompt)
      return;

    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }

    setIsUpdatingPrompt(true);
    const promptIdToUpdate = editingPromptId;

    // Optimistically update the prompt in the list immediately to prevent showing old value
    // Capture original prompt for potential rollback using functional update
    let originalPrompt: DefaultPrompt | undefined;
    setCreatedPrompts((prev) => {
      originalPrompt = prev.find((p) => p.id === promptIdToUpdate);
      return prev.map((p) =>
        p.id === promptIdToUpdate ? { ...p, prompt: promptValue } : p,
      );
    });

    // Clear editing state after optimistic update
    setEditingPromptId(null);

    try {
      const response = await updateDefaultPrompt({
        accountId: selectedAccount.id,
        promptId: promptIdToUpdate,
        prompt: promptValue,
      });
      // Update with the actual response data (in case server made any changes)
      setCreatedPrompts((prev) =>
        prev.map((p) => (p.id === promptIdToUpdate ? response.data : p)),
      );
    } catch (error) {
      console.error("Failed to update prompt:", error);
      // Revert optimistic update on error - restore original prompt
      if (originalPrompt) {
        setCreatedPrompts((prev) =>
          prev.map((p) => (p.id === promptIdToUpdate ? originalPrompt! : p)),
        );
      }
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  const clearDeleteTimer = (promptId?: string) => {
    if (promptId) {
      // Clear specific timer
      const timer = deleteTimersRef.current.get(promptId);
      if (timer) {
        clearTimeout(timer);
        deleteTimersRef.current.delete(promptId);
      }
    } else {
      // Clear all timers
      deleteTimersRef.current.forEach((timer) => clearTimeout(timer));
      deleteTimersRef.current.clear();
    }
  };

  const executeDelete = async (promptId: string) => {
    if (!selectedAccount?.id) return;

    try {
      await deleteDefaultPrompt({
        accountId: selectedAccount.id,
        promptId,
      });

      // If this was the selected prompt, find first prompt before removing
      const wasSelected = selectedPromptId === promptId;
      let firstPrompt: DefaultPrompt | null = null;

      if (wasSelected) {
        // Find first prompt from recommended or created (before deletion)
        firstPrompt =
          recommendedPrompts.length > 0
            ? recommendedPrompts[0]
            : createdPrompts.filter((p) => p.id !== promptId).length > 0
              ? createdPrompts.filter((p) => p.id !== promptId)[0]
              : null;
      }

      // Remove the prompt from the list
      setCreatedPrompts((prev) => prev.filter((p) => p.id !== promptId));

      // If this was the selected prompt, set first prompt as default
      if (wasSelected) {
        if (firstPrompt) {
          handleDefaultPromptChange(firstPrompt.id);
        } else {
          // No prompts left, clear selection
          setSelectedPromptId(null);
          saveToLocalStorage(CREATIVE_LIBRARY_STORAGE_KEYS.DEFAULT_PROMPT_ID, null);
        }
      }
    } catch (error) {
      console.error("Failed to delete prompt:", error);
    } finally {
      // Remove from pending deletes set
      setPendingDeleteIds((prev) => {
        const next = new Set(prev);
        next.delete(promptId);
        return next;
      });
      // Clear the timer for this prompt
      clearDeleteTimer(promptId);
    }
  };

  const handleDeletePrompt = (promptId: string) => {
    if (!selectedAccount?.id) return;

    // Clear any existing timer for this prompt
    clearDeleteTimer(promptId);

    // Add to pending delete set
    setPendingDeleteIds((prev) => new Set(prev).add(promptId));

    // Set timer to execute delete after 5 seconds
    const timer = setTimeout(() => {
      clearDeleteTimer(promptId);
      void executeDelete(promptId);
    }, 5000);

    deleteTimersRef.current.set(promptId, timer);
  };

  const handleUndoDelete = (promptId: string) => {
    clearDeleteTimer(promptId);
    setPendingDeleteIds((prev) => {
      const next = new Set(prev);
      next.delete(promptId);
      return next;
    });
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearDeleteTimer(); // Clear all timers
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, []);

  const handleCancelEdit = () => {
    // Clear any pending blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setEditingPromptId(null);
  };

  const handleCancelNewPrompt = () => {
    setIsAddingNewPrompt(false);
    setNewPromptText("");
  };

  const handleRecreate = async () => {
    if (!onCreateAd) return;

    setIsRecreating(true);
    handleSettingsOpenChange(false);

    // If no default prompt exists, fetch prompts and use first one
    let promptIdToUse = selectedPromptId;
    if (!promptIdToUse) {
      // Fetch prompts if not already fetched
      let promptsResponse: DefaultPromptsResponse | null = null;
      if (!hasFetchedPromptsRef.current) {
        promptsResponse = await loadPrompts();
      }

      // Use first prompt from fetched response or current state
      const firstPrompt =
        promptsResponse && promptsResponse.recommended.length > 0
          ? promptsResponse.recommended[0]
          : promptsResponse && promptsResponse.created.length > 0
            ? promptsResponse.created[0]
            : recommendedPrompts.length > 0
              ? recommendedPrompts[0]
              : createdPrompts.length > 0
                ? createdPrompts[0]
                : null;

      if (firstPrompt) {
        promptIdToUse = firstPrompt.id;
        handleDefaultPromptChange(firstPrompt.id);
      }
    }

    await onCreateAd({
      defaultPromptId: promptIdToUse,
      model,
      aspectRatio,
      variations,
      moodboardId: currentMoodboardId ?? null,
    });
    setIsRecreating(false);
  };

  // Render aspect ratio icon
  const renderRatioIcon = (ratio: AspectRatio) => {
    if (ratio === "Auto") {
      return null;
    }
    const [rwStr, rhStr] = ratio.split(":");
    const rw = Math.max(parseInt(rwStr || "1", 10) || 1, 1);
    const rh = Math.max(parseInt(rhStr || "1", 10) || 1, 1);
    const base = 14;
    const isWide = rw >= rh;
    const boxW = isWide ? base : Math.round(base * (rw / rh));
    const boxH = isWide ? Math.round(base * (rh / rw)) : base;
    return (
      <span className="inline-flex items-center justify-center w-[16px] h-[16px] rounded-sm border border-border-gray">
        <span
          className="block bg-border-gray"
          style={{ width: `${boxW}px`, height: `${boxH}px` }}
        />
      </span>
    );
  };

  const getSelectedModelLabel = () => {
    return MODEL_OPTIONS.find((opt) => opt.value === model)?.label || "GPT Image 1";
  };

  const getSelectedVariationsLabel = () => {
    return (
      VARIATIONS_OPTIONS.find((opt) => opt.value === variations)?.label || "4 Variations"
    );
  };

  // Shared helper to render check icon
  const renderCheckIcon = (isSelected: boolean) => {
    if (!isSelected) return null;
    return (
      <div className="w-[14px] h-[20px] flex items-center justify-center shrink-0">
        <RiCheckLine size={14} className="text-brand-orange shrink-0" />
      </div>
    );
  };

  // Shared base prompt item structure
  const PromptItemBase = ({
    promptItem,
    content,
    options = {},
  }: {
    promptItem: DefaultPrompt;
    content: React.ReactNode;
    options?: {
      onClick?: () => void;
      className?: string;
      showCheckIcon?: boolean;
      actions?: React.ReactNode;
      showTooltip?: boolean;
    };
  }) => {
    const isSelected = selectedPromptId === promptItem.id;
    const {
      onClick,
      className = "",
      showCheckIcon = true,
      actions,
      showTooltip = true,
    } = options;

    const containerClassName = cn(
      "h-[28px] w-full flex items-center px-[8px] py-0 rounded-[8px]",
      onClick && "group cursor-pointer",
      isSelected
        ? "bg-selected-gray font-medium"
        : onClick
          ? "hover:bg-hover-gray font-normal"
          : "",
      className,
    );

    const itemContent = (
      <div onClick={onClick} className={containerClassName}>
        <div className="w-full flex items-center gap-[6px]">
          {showCheckIcon && renderCheckIcon(isSelected)}
          {content}
          {actions && (
            <div className="hidden group-hover:flex items-center gap-1 shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>
    );

    if (!showTooltip) {
      return <div key={promptItem.id}>{itemContent}</div>;
    }

    return (
      <div key={promptItem.id}>
        <Tooltip>
          <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="max-w-[300px]">
            <p>{promptItem.prompt}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  };

  const renderPromptItem = (promptItem: DefaultPrompt) => (
    <PromptItemBase
      promptItem={promptItem}
      content={
        <span className="text-brand-primary-800 text-[12px] leading-[20px] line-clamp-1">
          {promptItem.prompt}
        </span>
      }
      options={{
        onClick: () => handleSelectPrompt(promptItem),
      }}
    />
  );

  const renderCreatedPromptItem = (promptItem: DefaultPrompt) => {
    const isPendingDelete = pendingDeleteIds.has(promptItem.id);
    const isEditing = editingPromptId === promptItem.id;

    // If this prompt is being edited, show input field
    if (isEditing) {
      return (
        <PromptItemBase
          promptItem={promptItem}
          content={
            <input
              key={promptItem.id}
              ref={editPromptInputRef}
              type="text"
              defaultValue={promptItem.prompt}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  void handleUpdatePrompt();
                } else if (e.key === "Escape") {
                  e.stopPropagation();
                  e.preventDefault();
                  handleCancelEdit();
                }
              }}
              onBlur={() => {
                // Clear any existing blur timeout
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                }

                // Store the current editing prompt ID at the time of blur
                const blurredPromptId = promptItem.id;

                // Small delay to allow click on other elements (like edit buttons)
                blurTimeoutRef.current = setTimeout(() => {
                  // Only cancel if:
                  // 1. We're still editing the same prompt (not switching to another)
                  // 2. The input is not focused (user didn't click back into it)
                  if (
                    editingPromptId === blurredPromptId &&
                    document.activeElement !== editPromptInputRef.current
                  ) {
                    handleCancelEdit();
                  }
                  blurTimeoutRef.current = null;
                }, 150);
              }}
              disabled={isUpdatingPrompt}
              className="flex-1 text-brand-primary-800 text-[12px] font-normal leading-[20px] bg-transparent border-none outline-none focus:ring-0 p-0"
              placeholder="Enter prompt..."
            />
          }
          options={{
            className: "bg-selected-gray",
            showTooltip: false,
          }}
        />
      );
    }

    // If this prompt is pending deletion, show with progress bar and undo
    if (isPendingDelete) {
      return (
        <div
          key={promptItem.id}
          className="w-full px-[8px] py-[4px] rounded-[8px] relative bg-hover-gray"
        >
          <div className="flex items-start gap-[6px]">
            <span className="flex-1 text-brand-primary-400 text-[12px] font-normal leading-[20px] line-clamp-1">
              {promptItem.prompt}
            </span>
            {/* Undo button */}
            <SqButton
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleUndoDelete(promptItem.id);
              }}
              size={"sm"}
              className="h-[24px] flex items-center py-0"
            >
              <RiArrowGoBackLine size={14} className="text-brand-primary-400 shrink-0" />
              <span>Undo</span>
            </SqButton>
          </div>
          {/* Progress bar - uses CSS transform animation for smooth GPU-accelerated progress */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full overflow-hidden">
            <div
              key={promptItem.id}
              className="h-full w-full bg-brand-orange origin-left"
              style={{
                transform: "scaleX(0)",
                animation: "deleteProgressScale 5s linear forwards",
              }}
            />
          </div>
        </div>
      );
    }

    // Normal state with edit/delete actions
    return (
      <PromptItemBase
        promptItem={promptItem}
        content={
          <span className="flex-1 text-brand-primary-800 text-[12px] leading-[20px] line-clamp-1">
            {promptItem.prompt}
          </span>
        }
        options={{
          onClick: () => handleSelectPrompt(promptItem),
          actions: (
            <>
              <SqButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditPrompt(promptItem);
                }}
                variant="outline"
                className="h-6 w-6 p-0 bg-white rounded-md"
              >
                <RiEditLine size={14} className="text-brand-primary-400" />
              </SqButton>
              <SqButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePrompt(promptItem.id);
                }}
                className="h-6 w-6 p-0 bg-[#FFF0F0] rounded-md"
                bgColor="#FFCCCC"
                style={{ backgroundColor: "#FFF0F0" }}
              >
                <RiDeleteBinLine size={14} className="text-destructive-700" />
              </SqButton>
            </>
          ),
        }}
      />
    );
  };

  return (
    <TooltipProvider>
      <div ref={createAdRef} className="text-white h-[30px] flex items-center gap-[6px] pl-[8px] pr-[3px] py-[8px] rounded-[8px] squircle-mask squircle-smooth-[1] squircle-radius-[8px] pointer-events-auto bg-[#00000099] backdrop-blur-sm hover:bg-[#000000CC] cursor-pointer [&:has(.group-settings:hover)]:text-white/60">
        {/* Reimagine glow effect – portalled into the Media container */}
        {mediaContainer && createPortal(
          <>
            <div className={`ai-glow-wrapper ai-glow--card pointer-events-none absolute inset-0 z-[50] rounded-[inherit] ${isRecreating ? "ai-glow-active" : ""}`}>
              <div className="ai-glow-edge ai-glow-edge-top" />
              <div className="ai-glow-edge ai-glow-edge-right" />
              <div className="ai-glow-edge ai-glow-edge-bottom" />
              <div className="ai-glow-edge ai-glow-edge-left" />
            </div>
          </>,
          mediaContainer,
        )}
        <div className="flex items-center">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="cursor-pointer"
                onClick={() => void handleRecreate()}
                disabled={isRecreating}
              >
                <div className="flex items-center gap-[6px]">
                  <RiBrushAiLine size={14} />
                  <span className="mt-[1px] text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
                    Reimagine
                  </span>
                </div>
              </button>
            </TooltipTrigger>
            <Portal>
              <TooltipContent side="bottom" sideOffset={10} align="start">
                <p>
                  Reimagine any static with your default prompt. <br /> Manage it from the
                  Settings button on the right.
                </p>
              </TooltipContent>
            </Portal>
          </Tooltip>
        </div>

        {/* Settings Popover */}
        <Popover modal open={settingsOpen} onOpenChange={handleSettingsOpenChange}>
          <PopoverTrigger asChild>
            <div className="group-settings">
              <button className="h-6 w-6 flex items-center justify-center squircle-mask squircle-smooth-[1] squircle-radius-[6px]  bg-black text-white/60 hover:text-white">
                <RiSettingsLine size={14} />
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={10}
            className="w-auto min-w-[200px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
          >
            {/* Default prompt */}
            <Popover
              open={defaultPromptOpen}
              onOpenChange={handleDefaultPromptOpenChange}
            >
              <PopoverTrigger asChild>
                <div
                  className="group cursor-pointer w-full pl-[8px] pr-[12px] py-[4px] rounded-[4px] hover:bg-hover-gray"
                  onMouseEnter={handleDefaultPromptHoverEnter}
                >
                  <div className="flex items-center gap-[6px]">
                    <RiArrowGoBackFill
                      size={14}
                      className="text-brand-primary-400 shrink-0"
                    />
                    <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                      Default prompt
                    </span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="right"
                sideOffset={8}
                className="w-[358px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
              >
                {/* Search Input */}
                <div className="mb-[4px]">
                  <div className="relative">
                    <LuSearch
                      size={14}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 text-brand-primary-400"
                    />
                    <Input
                      placeholder="Search"
                      value={promptSearchQuery}
                      onChange={(e) => setPromptSearchQuery(e.target.value)}
                      className="pl-7 h-[28px] text-[12px] text-brand-primary-new placeholder:text-brand-primary-400 rounded-[8px] bg-selected-gray flex items-center self-stretch pr-[8px] pt-[8px] pb-[6px] border-none focus:ring-0 shadow-none"
                    />
                  </div>
                </div>

                {/* Prompts List */}

                <div className="relative">
                  <div
                    ref={promptsContainerRef}
                    className="max-h-[300px] overflow-y-auto"
                  >
                    {isLoadingPrompts ? (
                      <div className="flex items-center justify-center py-8">
                        <LuLoaderCircle
                          size={20}
                          className="animate-spin text-brand-primary-400"
                        />
                      </div>
                    ) : (
                      <>
                        {/* Recommended Section */}
                        {filteredRecommendedPrompts.length > 0 && (
                          <Collapsible
                            open={recommendedOpen}
                            onOpenChange={setRecommendedOpen}
                          >
                            <CollapsibleTrigger asChild>
                              <div className="group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px] hover:bg-hover-gray">
                                <div className="flex items-center justify-between">
                                  <span className="text-brand-primary-400 text-[12px] font-medium leading-[20px]">
                                    Recommended
                                  </span>
                                  <LuChevronDown
                                    size={16}
                                    className={cn(
                                      "text-brand-primary-400 transition-transform duration-200",
                                      recommendedOpen ? "" : "-rotate-90",
                                    )}
                                  />
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              {filteredRecommendedPrompts.map(renderPromptItem)}
                            </CollapsibleContent>
                          </Collapsible>
                        )}

                        {/* Created by you Section */}
                        {(filteredCreatedPrompts.length > 0 || isAddingNewPrompt) && (
                          <Collapsible open={createdOpen} onOpenChange={setCreatedOpen}>
                            <CollapsibleTrigger asChild>
                              <div className="group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px] hover:bg-hover-gray">
                                <div className="flex items-center justify-between">
                                  <span className="text-brand-primary-400 text-[12px] font-medium leading-[20px]">
                                    Created by you
                                  </span>
                                  <LuChevronDown
                                    size={16}
                                    className={cn(
                                      "text-brand-primary-400 transition-transform duration-200",
                                      createdOpen ? "" : "-rotate-90",
                                    )}
                                  />
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              {filteredCreatedPrompts.map(renderCreatedPromptItem)}
                              {/* New prompt input field */}
                              {isAddingNewPrompt && (
                                <div className="h-[28px] w-full flex items-center px-[8px] py-[4px]">
                                  <div className="w-full flex items-start justify-between gap-2">
                                    <input
                                      ref={newPromptInputRef}
                                      type="text"
                                      value={newPromptText}
                                      onChange={(e) => setNewPromptText(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          void handleCreatePrompt();
                                        } else if (e.key === "Escape") {
                                          handleCancelNewPrompt();
                                        }
                                      }}
                                      onBlur={() => {
                                        // Small delay to allow click on other elements
                                        setTimeout(() => {
                                          if (!newPromptText.trim()) {
                                            handleCancelNewPrompt();
                                          }
                                        }, 150);
                                      }}
                                      disabled={isCreatingPrompt}
                                      className="flex-1 text-brand-primary-800 text-[12px] font-normal leading-[20px] bg-transparent border-none outline-none focus:ring-0 p-0"
                                      placeholder="Define your prompt"
                                    />
                                    {isCreatingPrompt && (
                                      <LuLoaderCircle
                                        size={14}
                                        className="animate-spin text-brand-primary-400 shrink-0"
                                      />
                                    )}
                                  </div>
                                </div>
                              )}
                            </CollapsibleContent>
                          </Collapsible>
                        )}

                        {/* Empty state */}
                        {filteredRecommendedPrompts.length === 0 &&
                          filteredCreatedPrompts.length === 0 &&
                          promptSearchQuery.trim() && (
                            <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[11px]">
                              No results found
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>

                {/* New Prompt Button */}
                <div
                  onClick={handleNewPrompt}
                  className="h-[28px] cursor-pointer w-full flex items-center px-[8px] py-0 mt-1 rounded-[8px] bg-[#FF45000A] hover:bg-[#FF450014]"
                >
                  <div className="flex items-center gap-[6px]">
                    <LuPlus size={14} className="text-brand-orange shrink-0" />
                    <span className="text-brand-orange text-[12px] font-normal leading-[20px]">
                      New Prompt
                    </span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Custom prompt */}
            <div
              onClick={handleCustomPromptClick}
              className="group cursor-pointer w-full pl-[8px] pr-[12px] py-[4px] rounded-[4px] hover:bg-hover-gray"
            >
              <div className="flex items-center gap-[6px]">
                <RiPencilLine size={14} className="text-brand-primary-400 shrink-0" />
                <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                  Custom prompt
                </span>
              </div>
            </div>

            {/* Model */}
            <Popover open={modelOpen} onOpenChange={setModelOpen}>
              <PopoverTrigger asChild>
                <div
                  className="group cursor-pointer w-full pl-[8px] pr-[12px] py-[4px] rounded-[4px] hover:bg-hover-gray"
                  onMouseEnter={handleModelHoverEnter}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-[6px]">
                      <RiBrushAiLine
                        size={14}
                        className="text-brand-primary-400 shrink-0"
                      />
                      <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                        Model
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-brand-primary-400 text-[12px] font-normal leading-[20px]">
                        {getSelectedModelLabel()}
                      </span>
                    </div>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="right"
                sideOffset={8}
                className="w-auto min-w-[160px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
              >
                {MODEL_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => {
                      handleModelChange(opt.value);
                      setModelOpen(false);
                    }}
                    className={cn(
                      "group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px]",
                      opt.value === model ? "bg-selected-gray" : "hover:bg-hover-gray",
                    )}
                  >
                    <div className="flex items-center gap-[6px]">
                      <opt.icon
                        size={14}
                        className={cn(
                          "text-brand-primary-400 shrink-0",
                          opt.value === model ? "text-brand-orange" : "",
                        )}
                      />
                      <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                        {opt.label}
                      </span>
                    </div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>

            {/* Aspect Ratio */}
            <Popover open={aspectRatioOpen} onOpenChange={setAspectRatioOpen}>
              <PopoverTrigger asChild>
                <div
                  className="group cursor-pointer w-full pl-[8px] pr-[12px] py-[4px] rounded-[4px] hover:bg-hover-gray"
                  onMouseEnter={handleAspectRatioHoverEnter}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-[6px]">
                      <RiAspectRatioLine
                        size={14}
                        className="text-brand-primary-400 shrink-0"
                      />
                      <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                        Aspect ratio
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-brand-primary-400 text-[12px] font-normal leading-[20px]">
                        {aspectRatio}
                      </span>
                    </div>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="right"
                sideOffset={8}
                className="w-auto min-w-[120px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
              >
                {ASPECT_RATIO_OPTIONS.map((opt) => (
                  <div
                    key={opt}
                    onClick={() => {
                      handleAspectRatioChange(opt);
                      setAspectRatioOpen(false);
                    }}
                    className={cn(
                      "group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px]",
                      opt === aspectRatio
                        ? "bg-selected-gray font-medium"
                        : "hover:bg-hover-gray font-normal",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {renderCheckIcon(opt === aspectRatio)}
                      {renderRatioIcon(opt)}
                      <span className="mt-[2px] text-brand-primary-800 text-[12px] leading-[20px]">
                        {opt}
                      </span>
                    </div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>

            {/* Variations */}
            <Popover open={variationsOpen} onOpenChange={setVariationsOpen}>
              <PopoverTrigger asChild>
                <div
                  className="group cursor-pointer w-full pl-[8px] pr-[12px] py-[4px] rounded-[4px] hover:bg-hover-gray"
                  onMouseEnter={handleVariationsHoverEnter}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-[6px]">
                      <RiApps2AiLine
                        size={14}
                        className="text-brand-primary-400 shrink-0"
                      />
                      <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                        Variations
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-brand-primary-400 text-[12px] font-normal leading-[20px]">
                        {getSelectedVariationsLabel()}
                      </span>
                    </div>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="right"
                sideOffset={8}
                className="w-auto min-w-[140px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
              >
                {VARIATIONS_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => {
                      handleVariationsChange(opt.value);
                      setVariationsOpen(false);
                    }}
                    className={cn(
                      "group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px]",
                      opt.value === variations
                        ? "bg-selected-gray font-medium"
                        : "hover:bg-hover-gray font-normal",
                    )}
                  >
                    <div className="flex items-center gap-[6px]">
                      <opt.icon
                        size={14}
                        className={cn(
                          "text-brand-primary-400 shrink-0",
                          opt.value === variations ? "text-brand-orange" : "",
                        )}
                      />
                      <span className="mt-[2px] text-brand-primary-800 text-[12px] leading-[20px]">
                        {opt.label}
                      </span>
                    </div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>

            {/* Style */}
            {shouldShowStyleSetting && (
              <Popover open={styleOpen} onOpenChange={setStyleOpen}>
                <PopoverTrigger asChild>
                  <div
                    className="group cursor-pointer w-full pl-[8px] pr-[12px] py-[4px] rounded-[4px] hover:bg-hover-gray"
                    onMouseEnter={handleStyleHoverEnter}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-[6px]">
                        <RiQuillPenAiLine
                          size={14}
                          className="text-brand-primary-400 shrink-0"
                        />
                        <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                          Style
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-brand-primary-400 text-[12px] font-normal leading-[20px] max-w-[120px] truncate">
                          {selectedStyleLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="right"
                  sideOffset={8}
                  className="w-[220px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white drop-shadow-md"
                >
                  {/* Search */}
                  <div className="mb-[4px]">
                    <div className="relative">
                      <LuSearch
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-brand-primary-400"
                      />
                      <Input
                        placeholder="Search"
                        value={styleSearchQuery}
                        onChange={(e) => setStyleSearchQuery(e.target.value)}
                        className="pl-7 h-[28px] text-[12px] text-brand-primary-new placeholder:text-brand-primary-400 rounded-[8px] bg-selected-gray flex items-center self-stretch pr-[8px] pt-[8px] pb-[6px] border-none focus:ring-0 shadow-none"
                      />
                    </div>
                  </div>

                  {moodboardsLoading ? (
                    <div className="px-[8px] py-[8px] text-brand-primary-400 text-[11px]">
                      Loading…
                    </div>
                  ) : (
                    <div className="max-h-[220px] overflow-y-auto">
                      <div
                        onClick={() => handleMoodboardChange(null)}
                        className={cn(
                          "group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px]",
                          !currentMoodboardId
                            ? "bg-selected-gray font-medium"
                            : "hover:bg-hover-gray font-normal",
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {renderCheckIcon(!currentMoodboardId)}
                          <span className="mt-[2px] text-brand-primary-800 text-[12px] leading-[20px]">
                            None
                          </span>
                        </div>
                      </div>

                      {filteredMoodboards.length === 0 ? (
                        <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[11px]">
                          No styles found
                        </div>
                      ) : (
                        filteredMoodboards.map((m) => (
                          <div
                            key={m.id}
                            onClick={() => handleMoodboardChange(m.id)}
                            className={cn(
                              "group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px]",
                              m.id === currentMoodboardId
                                ? "bg-selected-gray font-medium"
                                : "hover:bg-hover-gray font-normal",
                            )}
                          >
                            <div className="flex items-center gap-2">
                              {renderCheckIcon(m.id === currentMoodboardId)}
                              <span className="mt-[2px] text-brand-primary-800 text-[12px] leading-[20px] truncate">
                                {m.name}
                              </span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </TooltipProvider>
  );
};

// Memoize component to prevent unnecessary re-renders
export default memo(CreateAd, (prevProps, nextProps) => {
  // Re-render only if these props change
  return (
    prevProps.ad.id === nextProps.ad.id &&
    prevProps.currentModel === nextProps.currentModel &&
    prevProps.currentAspectRatio === nextProps.currentAspectRatio &&
    prevProps.currentVariations === nextProps.currentVariations &&
    prevProps.currentDefaultPromptId === nextProps.currentDefaultPromptId &&
    prevProps.currentMoodboardId === nextProps.currentMoodboardId &&
    prevProps.moodboardsLoading === nextProps.moodboardsLoading &&
    prevProps.moodboards === nextProps.moodboards &&
    prevProps.forcedVisibility === nextProps.forcedVisibility
  );
});
